import { Input } from '@1trading/react';
import { Image, LogOut, Moon, Search, Square, Sun } from 'lucide-react';
import { useAuth } from 'react-oidc-context';

import { Logo } from '@/components/layout/logo';
import { useIconStyle } from '@/hooks/use-icon-style';
import { useTheme } from '@/hooks/use-theme';
import type { WorkforceSession } from '@/types/catalog';

type HeaderProps = {
  session: WorkforceSession;
  search: string;
  onSearchChange: (value: string) => void;
};

const orgFromEmail = (email: string) => email.split('@')[1]?.split('.')[0] ?? '';

export const Header = ({ session, search, onSearchChange }: HeaderProps) => {
  const { theme, toggleTheme } = useTheme();
  const { iconStyle, toggleIconStyle } = useIconStyle();
  const auth = useAuth();

  return (
    <header className="sticky top-0 z-20 grid h-[var(--header-height-sm)] grid-cols-[auto_1fr_auto] items-center gap-4 bg-card px-4 md:h-[var(--header-height-md)] md:gap-8 md:border-border md:border-b md:px-8 md:dark:border-border/20 lg:px-12">
      <Logo theme={theme} />
      <div className="relative w-full max-w-2xs justify-self-center">
        <Search aria-hidden className="-translate-y-1/2 absolute top-1/2 left-3 size-4 text-muted-foreground" />
        <Input type="search" value={search} onChange={(event) => onSearchChange(event.target.value)} placeholder="Search apps" aria-label="Search apps" className="pl-9" />
      </div>
      <aside className="flex items-center gap-3">
        <button
          type="button"
          onClick={toggleIconStyle}
          aria-label={iconStyle === 'image' ? 'Show block colours' : 'Show icon images'}
          aria-pressed={iconStyle === 'image'}
          className="rounded-lg border p-2 text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-2 focus-visible:outline-ring"
        >
          {iconStyle === 'image' ? <Square className="size-4" aria-hidden /> : <Image className="size-4" aria-hidden />}
        </button>
        <button
          type="button"
          onClick={toggleTheme}
          aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
          className="rounded-lg border p-2 text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-2 focus-visible:outline-ring"
        >
          {theme === 'dark' ? <Sun className="size-4" aria-hidden /> : <Moon className="size-4" aria-hidden />}
        </button>
        <div className="flex items-center gap-2.5">
          <span aria-hidden className="flex size-9 items-center justify-center rounded-full bg-primary font-medium text-primary-foreground text-sm">
            {session.name[0]}
          </span>
          <div className="hidden leading-tight sm:block">
            <p className="font-medium text-sm">{session.name.split(' ')[0]}</p>
            <p className="max-w-48 truncate text-muted-foreground text-xs" title={session.email}>
              {session.email || orgFromEmail(session.email)}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => void auth.signoutRedirect()}
          aria-label="Sign out"
          className="rounded-lg border p-2 text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-2 focus-visible:outline-ring"
        >
          <LogOut className="size-4" aria-hidden />
        </button>
      </aside>
    </header>
  );
};
